import { BoardConfigurationData, SegmentData, SegmentShapeType } from './types';

const SEGMENT_BACKGROUND_COLOR = '#fff0';
const SEGMENT_KEY_BACKGROUND_COLOR = '#ccc6';
const SEGMENT_HIGHLIGHT_BACKGROUND_COLOR = '#bcf6';
const SEGMENT_COLOR = '#000a';
const SEGMENT_HIGHLIGHT_COLOR = '#000';

type SegmentProps = {
  data: SegmentData;
  boardConfiguration: BoardConfigurationData;
  posX: number;
  posY: number;
  isHighlighted: boolean;
  onSegmentMouseEnter?: (points: string) => void;
  onSegmentMouseLeave?: () => void;
};

const getCenter = (points: string) => {
  const coords = points.split(' ').map((p) => p.split(',').map(Number));
  const xs = coords.map((c) => c[0]);
  const ys = coords.map((c) => c[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
};

const getPointsByShapeType = (
  type: SegmentShapeType,
  width: number,
  height: number,
  marginLeft: number,
  marginTop: number,
  tiltValue: number,
  extendedTiltValue: number,
) => {
  // to display a segment, we use a list of points that represent the four edges of the polygon
  // we have x1,y1 x2,y2 x3,y3 x4,y4
  switch (type) {
    case SegmentShapeType.Key:
      return `${marginLeft},${marginTop} ${marginLeft + width + tiltValue},${marginTop} ${marginLeft + width},${marginTop + height} ${marginLeft + tiltValue},${marginTop + height}`;
    case SegmentShapeType.KeyInverted:
      return `${marginLeft + tiltValue},${marginTop} ${marginLeft + width},${marginTop} ${marginLeft + width + tiltValue},${marginTop + height} ${marginLeft},${marginTop + height}`;
    case SegmentShapeType.HalfKey:
      return `${marginLeft},${marginTop} ${marginLeft + width + extendedTiltValue},${marginTop} ${marginLeft + width},${marginTop + height} ${marginLeft + extendedTiltValue},${marginTop + height}`;
    case SegmentShapeType.TiltedLeft:
      return `${marginLeft},${marginTop} ${marginLeft + width},${marginTop} ${marginLeft + width + tiltValue},${marginTop + height} ${marginLeft + tiltValue},${marginTop + height}`;
    case SegmentShapeType.TiltedLeftExtended:
      return `${marginLeft},${marginTop} ${marginLeft + width},${marginTop} ${marginLeft + width + extendedTiltValue},${marginTop + height} ${marginLeft + tiltValue},${marginTop + height}`;
    case SegmentShapeType.TiltedRight:
      return `${marginLeft + tiltValue},${marginTop} ${marginLeft + width + tiltValue},${marginTop} ${marginLeft + width},${marginTop + height} ${marginLeft},${marginTop + height}`;
    case SegmentShapeType.TiltedRightExtended:
      return `${marginLeft + extendedTiltValue},${marginTop} ${marginLeft + width + tiltValue},${marginTop} ${marginLeft + width},${marginTop + height} ${marginLeft},${marginTop + height}`;
    default:
      throw new Error('shape not found');
  }
};

export const Segment = ({
  data,
  boardConfiguration,
  posX,
  posY,
  isHighlighted,
  onSegmentMouseEnter,
  onSegmentMouseLeave,
}: SegmentProps) => {
  const points = getPointsByShapeType(
    data.shapeType,
    boardConfiguration.segment.width,
    boardConfiguration.segment.height,
    posX,
    posY,
    boardConfiguration.segment.tilt.value,
    boardConfiguration.segment.tilt.extendedValue,
  );

  // calculate center of the segment to display the segment type
  const center = getCenter(points);

  let backgroundColor =
    data.label === boardConfiguration.segment.keyLabel
      ? SEGMENT_KEY_BACKGROUND_COLOR
      : SEGMENT_BACKGROUND_COLOR;
  let segmentColor = SEGMENT_COLOR;

  if (isHighlighted) {
    backgroundColor = SEGMENT_HIGHLIGHT_BACKGROUND_COLOR;
    segmentColor = SEGMENT_HIGHLIGHT_COLOR;
  }

  return (
    <>
      <g>
        {/* display the segment */}
        <polygon
          points={points}
          fill={backgroundColor}
          stroke={segmentColor}
          data-id={data.id}
          onMouseEnter={() => onSegmentMouseEnter?.(points)}
          onMouseLeave={onSegmentMouseLeave}
        />
        {/* display a circle on the left side of the segment type */}
        <circle cx={center.x - 15} cy={center.y} r={2} fill={segmentColor} />
        <text
          x={center.x}
          y={center.y + 5}
          textAnchor="middle"
          style={{ pointerEvents: 'none' }}
          fill={segmentColor}
        >
          {data.label}
        </text>
        {/* display a circle on the right side of the segment type */}
        <circle cx={center.x + 15} cy={center.y} r={2} fill={segmentColor} />
      </g>
      {data.defects.map((defect) => {
        const projectedPointsString = defect.strokes
          .map((p) => {
            const absoluteX = posX + p.x * boardConfiguration.segment.width;
            const absoluteY = posY + p.y * boardConfiguration.segment.height;
            return `${absoluteX},${absoluteY}`;
          })
          .join(' ');

        return (
          <polyline
            key={defect.id}
            points={projectedPointsString}
            fill="none"
            stroke={defect.color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      })}
    </>
  );
};
